/**
 * Schema F24 (padre + righe) e helper di migrazione/derivazione.
 *
 * FASE 3a: i campi legacy (tipo/importo/codiceTributo/anno) sono AUTORITATIVI;
 * i campi nuovi (righe/totale/annoRiferimento/categoria) sono DERIVATI da essi.
 * In FASE 3b le righe diventeranno autoritative (scritte dal modale multi-riga).
 */

// Sezioni della riga F24
export const F24_SEZIONI = { ERARIO: "erario", INPS: "inps" };

// Categoria F24 orientata alla scadenza (nuovo enum, campo `categoria`)
export const F24_CATEGORIE = {
  SALDO: "saldo",
  ACCONTO_GIUGNO: "acconto_giugno",
  ACCONTO_NOVEMBRE: "acconto_novembre",
  BOLLO: "bollo",
  ALTRO: "altro",
};

// Riga F24 di default (schema FIX 3)
export const DEFAULT_F24_RIGA = {
  sezione: "erario",
  codiceTributo: "",
  rateazione: null,            // es. "0104" (rata 1 di 4)
  periodoRiferimentoDa: null,  // mm/aaaa
  periodoRiferimentoA: null,
  importoDebito: 0,
  importoCredito: 0,
  codiceSede: null,            // INPS
  causaleContributo: null,     // INPS
};

export function createF24Riga(partial = {}) {
  return { ...DEFAULT_F24_RIGA, ...partial };
}

// Totale F24 = somma (debiti − crediti) delle righe, arrotondata ai centesimi.
export function computeF24Totale(righe) {
  if (!Array.isArray(righe)) return 0;
  const net = righe.reduce(
    (s, r) => s + (Number(r.importoDebito) || 0) - (Number(r.importoCredito) || 0),
    0
  );
  return Math.round(net * 100) / 100;
}

// Sezione derivata dal vecchio tipo (imposta → erario, inps → inps).
export function inferSezione(tipoLegacy) {
  return tipoLegacy === "acconto_inps" || tipoLegacy === "saldo_inps"
    ? F24_SEZIONI.INPS
    : F24_SEZIONI.ERARIO;
}

// Categoria (nuovo enum) derivata da vecchio tipo + mese scadenza + codice tributo.
export function inferCategoria(tipoLegacy, dataScadenza, codiceTributo) {
  if ((codiceTributo || "").trim() === "2501") return F24_CATEGORIE.BOLLO;
  if (tipoLegacy === "saldo_imposta" || tipoLegacy === "saldo_inps") {
    return F24_CATEGORIE.SALDO;
  }
  if (tipoLegacy === "acconto_imposta" || tipoLegacy === "acconto_inps") {
    const mese = dataScadenza ? new Date(dataScadenza).getMonth() + 1 : null;
    return mese === 11 ? F24_CATEGORIE.ACCONTO_NOVEMBRE : F24_CATEGORIE.ACCONTO_GIUGNO;
  }
  return F24_CATEGORIE.ALTRO;
}

// Stato derivato a runtime (NON persistito): pagato / scaduto / da_pagare.
export function getStatoF24(f, today) {
  const t = today || new Date().toISOString().split("T")[0];
  if (f?.dataPagamento) return "pagato";
  if (f?.dataScadenza && f.dataScadenza < t) return "scaduto";
  return "da_pagare";
}

// Deriva i campi padre dai dati delle righe (FASE 3b: righe autoritative).
function normalizeFromRighe(f) {
  const righe = (f.righe || []).map(createF24Riga);
  const totale = computeF24Totale(righe);
  const anno =
    Number(f.annoRiferimento) ||
    Number(f.anno) ||
    (f.dataScadenza ? new Date(f.dataScadenza).getFullYear() : new Date().getFullYear());
  return {
    ...f,
    schemaVersion: 2,
    righe,
    totale,
    importo: totale,       // mirror legacy (riepiloghi, ravvedimento)
    annoRiferimento: anno,
    anno,                  // mirror legacy (f24ForYear, display)
    categoria: f.categoria || F24_CATEGORIE.ALTRO,
    codiceTributo: (righe[0]?.codiceTributo || "").trim(), // mirror best-effort
  };
}

/**
 * Deriva lo schema padre+righe dai campi LEGACY (autoritativi).
 * Usato per i record v1 (creati prima del modale multi-riga).
 */
function wrapLegacyF24(f) {
  const anno =
    Number(f.anno) ||
    Number(f.annoRiferimento) ||
    (f.dataScadenza ? new Date(f.dataScadenza).getFullYear() : new Date().getFullYear());
  const importo = Number(f.importo) || Number(f.totale) || 0;
  const riga = createF24Riga({
    sezione: inferSezione(f.tipo),
    codiceTributo: (f.codiceTributo || "").trim(),
    importoDebito: importo,
  });
  const righe = [riga];
  const totale = computeF24Totale(righe);
  return {
    ...f,
    anno,                  // mirror legacy (f24ForYear, display)
    annoRiferimento: anno,
    categoria: inferCategoria(f.tipo, f.dataScadenza, f.codiceTributo),
    importo: totale,       // mirror legacy (riepiloghi, ravvedimento)
    totale,
    righe,
  };
}

/**
 * Normalizza un record F24 allo schema padre+righe.
 * - schemaVersion >= 2 (modale multi-riga): le righe sono AUTORITATIVE.
 * - altrimenti (record legacy v1): le righe sono DERIVATE dai campi legacy.
 */
export function migrateF24Record(f) {
  if (!f) return f;
  if (f.schemaVersion >= 2 && Array.isArray(f.righe)) {
    return normalizeFromRighe(f);
  }
  return wrapLegacyF24(f);
}

export function migrateF24List(list) {
  return (list || []).map(migrateF24Record);
}

// Aggrega gli F24 PAGATI per anno di riferimento → struttura compatibile con fiscalByYear.
// acconto_giugno/novembre → accontiVersati / inpsAccontiVersati per slot+sezione.
// saldo → saldoVersato {imposta(erario), inps}. bollo/altro → ignorati.
// Gli anni con F24 ma senza pagamenti producono valori 0 (così unpay/edit resettano).
export function aggregateF24PaidByYear(f24List) {
  const r2 = (n) => Math.round(n * 100) / 100;
  const byYear = {};
  for (const f of f24List || []) {
    const year = Number(f.annoRiferimento) || Number(f.anno);
    if (!year) continue;
    if (!byYear[year]) {
      byYear[year] = {
        accontiVersati: { giugno: 0, novembre: 0 },
        inpsAccontiVersati: { giugno: 0, novembre: 0 },
        saldoVersato: { imposta: 0, inps: 0 },
      };
    }
    if (!f.dataPagamento) continue; // solo F24 pagati contribuiscono
    let erario = 0, inps = 0;
    for (const rg of Array.isArray(f.righe) ? f.righe : []) {
      const net = (Number(rg.importoDebito) || 0) - (Number(rg.importoCredito) || 0);
      if (rg.sezione === F24_SEZIONI.INPS) inps += net; else erario += net;
    }
    const s = byYear[year];
    if (f.categoria === F24_CATEGORIE.ACCONTO_GIUGNO) {
      s.accontiVersati.giugno += erario; s.inpsAccontiVersati.giugno += inps;
    } else if (f.categoria === F24_CATEGORIE.ACCONTO_NOVEMBRE) {
      s.accontiVersati.novembre += erario; s.inpsAccontiVersati.novembre += inps;
    } else if (f.categoria === F24_CATEGORIE.SALDO) {
      s.saldoVersato.imposta += erario; s.saldoVersato.inps += inps;
    }
  }
  for (const y of Object.keys(byYear)) {
    const s = byYear[y];
    s.accontiVersati = { giugno: r2(s.accontiVersati.giugno), novembre: r2(s.accontiVersati.novembre) };
    s.inpsAccontiVersati = { giugno: r2(s.inpsAccontiVersati.giugno), novembre: r2(s.inpsAccontiVersati.novembre) };
    s.saldoVersato = { imposta: r2(s.saldoVersato.imposta), inps: r2(s.saldoVersato.inps) };
  }
  return byYear;
}
